import { NO_DEPENDENTS_SENTINEL } from "./AddressableSetup.constants";

/**
 * Famílias de dependente da Tabela 07 do eSocial, uma por tipo. É o que a tela de
 * endereçabilidade oferece e grava em `allowedDependentTypes`.
 *
 * Espelha `dependent-family.ts` do backend: o `value` é o rótulo canônico que o backend
 * classifica, e `normalizeDependentType` segue a mesma normalização (minúsculas, sem
 * acento, sem prefixo "03 - ", espaços colapsados).
 *
 * why: até 22/10/2026 a lista tinha 14 rótulos em três vocabulários, e cinco deles
 * ("01 - Cônjuge", "03 - Filho(a)", "06 - Enteado(a)", "09 - Pai/Mãe", "99 - Outros")
 * não existiam em massa nenhuma. Quem os marcava escolhia "Nenhum" sem saber. Listas
 * antigas continuam válidas: ao abrir, cada rótulo gravado é levado para a família.
 */
export type DependentFamily =
  | "CONJUGE"
  | "COMPANHEIRO"
  | "EX_CONJUGE"
  | "FILHO_ENTEADO"
  | "FILHO_UNIVERSITARIO"
  | "IRMAO_NETO_BISNETO"
  | "PAIS_AVOS_BISAVOS"
  | "MENOR_POBRE"
  | "INCAPAZ"
  | "AGREGADO_OUTROS";

export interface DependentFamilyOption {
  family: DependentFamily;
  /** Gravado em allowedDependentTypes. */
  value: string;
  /** Mostrado na tela. */
  label: string;
}

export const DEPENDENT_FAMILY_OPTIONS: DependentFamilyOption[] = [
  { family: "CONJUGE", value: "Cônjuge", label: "Cônjuge" },
  {
    family: "COMPANHEIRO",
    value: "Companheiro(a) com o(a) qual tenha filho ou viva há mais de 5 (cinco) anos ou possua declaração de união estável",
    label: "Companheiro(a) ou união estável",
  },
  { family: "EX_CONJUGE", value: "Ex-cônjuge", label: "Ex-cônjuge" },
  { family: "FILHO_ENTEADO", value: "Filho(a) ou enteado(a)", label: "Filho(a) ou enteado(a)" },
  {
    family: "FILHO_UNIVERSITARIO",
    value: "Filho(a) ou enteado(a), universitário(a) ou cursando escola técnica de 2º grau",
    label: "Filho(a) ou enteado(a) universitário(a) ou em escola técnica",
  },
  {
    family: "IRMAO_NETO_BISNETO",
    value: "Irmão(ã), neto(a) ou bisneto(a) sem arrimo dos pais, do(a) qual detenha a guarda judicial",
    label: "Irmão(ã), neto(a) ou bisneto(a) sob guarda judicial",
  },
  { family: "PAIS_AVOS_BISAVOS", value: "Pais, avós e bisavós", label: "Pais, avós e bisavós" },
  {
    family: "MENOR_POBRE",
    value: "Menor pobre do qual detenha a guarda judicial",
    label: "Menor pobre sob guarda judicial",
  },
  {
    family: "INCAPAZ",
    value: "A pessoa absolutamente incapaz, da qual seja tutor ou curador",
    label: "Pessoa incapaz sob tutela ou curatela",
  },
  { family: "AGREGADO_OUTROS", value: "Agregado/Outros", label: "Agregado ou outros" },
];

/** Rótulos antigos da própria tela e os que a massa grava, por família. */
const LEGACY_LABELS: Record<DependentFamily, string[]> = {
  CONJUGE: ["01 - Cônjuge"],
  COMPANHEIRO: ["Companheiro(a) com o(a) qual tenha filho ou viva há mais de 5 (cinco) anos"],
  EX_CONJUGE: ["Ex-cônjuge que receba pensão de alimentos"],
  FILHO_ENTEADO: [
    "03 - Filho(a)",
    "06 - Enteado(a)",
    "Filho(a)",
    "Filho(a) ou enteado(a) até 21 (vinte e um) anos",
  ],
  FILHO_UNIVERSITARIO: [
    "Filho(a) ou enteado(a), universitário(a) ou cursando escola técnica de 2o grau",
    "Filho(a) ou enteado(a) universitário(a) ou cursando escola técnica de 2º grau, até 24 (vinte e quatro) anos",
  ],
  IRMAO_NETO_BISNETO: [
    "Irmão(ã), neto(a) ou bisneto(a) sem arrimo dos pais, do(a) qual detenha a guarda judicial, até 21 (vinte e um) anos",
    "Irmão(ã), neto(a) ou bisneto(a) sem arrimo dos pais, universitário(a) ou cursando escola técnica de 2° grau, do(a) qual detenha a guarda judicial",
  ],
  PAIS_AVOS_BISAVOS: ["09 - Pai/Mãe"],
  MENOR_POBRE: ["Menor pobre, até 21 (vinte e um) anos, que crie e eduque e do qual detenha a guarda judicial"],
  INCAPAZ: [],
  AGREGADO_OUTROS: ["99 - Outros"],
};

const ACCENTS_FROM = "áàâãäéèêëíìîïóòôõöúùûüçñº°";
const ACCENTS_TO = "aaaaaeeeeiiiiooooouuuucnoo";

export function normalizeDependentType(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const translated = Array.from(String(value).toLowerCase())
    .map((ch) => {
      const i = ACCENTS_FROM.indexOf(ch);
      return i >= 0 ? ACCENTS_TO[i] : ch;
    })
    .join("");
  const result = translated.trim().replace(/^[0-9]+ *- */, "").replace(/ +/g, " ").trim();
  return result === "" ? null : result;
}

const familyOfNormalized = new Map<string, DependentFamily>();
DEPENDENT_FAMILY_OPTIONS.forEach((opt) => {
  [opt.value, ...LEGACY_LABELS[opt.family]].forEach((label) => {
    const n = normalizeDependentType(label);
    if (n && !familyOfNormalized.has(n)) familyOfNormalized.set(n, opt.family);
  });
});

export function dependentFamilyOf(label: string | null | undefined): DependentFamily | null {
  const n = normalizeDependentType(label);
  return n === null ? null : familyOfNormalized.get(n) ?? null;
}

/**
 * Leva uma lista gravada (qualquer vocabulário) para os valores canônicos das famílias,
 * sem repetir. O sentinela "Nenhum" é preservado; rótulo que não casa com família nenhuma
 * é descartado (o backend também não o reconhece).
 */
export function toCanonicalDependentTypes(saved: string[] | null | undefined): string[] {
  if (!saved || saved.length === 0) return [];
  if (saved.includes(NO_DEPENDENTS_SENTINEL)) return [NO_DEPENDENTS_SENTINEL];
  const out: string[] = [];
  saved.forEach((label) => {
    const family = dependentFamilyOf(label);
    if (!family) return;
    const value = DEPENDENT_FAMILY_OPTIONS.find((o) => o.family === family)!.value;
    if (!out.includes(value)) out.push(value);
  });
  return out;
}
